import { rest } from "@auto/services";
import { getServiceUri } from "@auto/metadata";

import {
  createEmptyReportingFilter,
  normalizeReportingFilter,
} from "@src/reporting/model/reportingFilters.mjs";

const ENTITY_LIMIT = 5000;
const OPERATOR_LIMIT = 100;

const request = async (path, method = "GET", body = null) => (
  await rest.request(getServiceUri() + path, method, body)
);

export const emptyReportingFilter = createEmptyReportingFilter;
export { normalizeReportingFilter };

export const loadReportingApplications = async (filter, { offset = 0, limit = 20 } = {}) => (
  await request(
    `reporting/applications?offset=${Number(offset)}&limit=${Number(limit)}`,
    "POST",
    normalizeReportingFilter(filter),
  )
);

export const loadReportingSummary = async (filter) => (
  await request("reporting/summary", "POST", normalizeReportingFilter(filter))
);

export const loadDistinctApplicantsKpi = async (filter) => (
  await request("reporting/overview/kpi/distinct-applicants", "POST", normalizeReportingFilter(filter))
);

export const loadRegisteredVotersKpi = async (filter) => (
  await request("reporting/overview/kpi/registered-voters", "POST", normalizeReportingFilter(filter))
);

export const loadActiveOperatorsKpi = async (filter) => (
  await request("reporting/overview/kpi/active-operators", "POST", normalizeReportingFilter(filter))
);

export const loadActiveCentersKpi = async (filter) => (
  await request("reporting/overview/kpi/active-centers", "POST", normalizeReportingFilter(filter))
);

export const loadConfiguredCentersKpi = async (filter) => (
  await request("reporting/overview/kpi/configured-centers", "POST", normalizeReportingFilter(filter))
);

export const loadCapacityUtilizationKpi = async (filter) => (
  await request("reporting/overview/kpi/capacity-utilization", "POST", normalizeReportingFilter(filter))
);

export const loadFirstApplicationKpi = async (filter) => (
  await request("reporting/overview/kpi/first-application", "POST", normalizeReportingFilter(filter))
);

export const loadLastApplicationKpi = async (filter) => (
  await request("reporting/overview/kpi/last-application", "POST", normalizeReportingFilter(filter))
);

export const loadRegisteredVoterSexDistribution = async (filter) => (
  await request("reporting/overview/registered-voters/sex", "POST", normalizeReportingFilter(filter))
);

export const loadRegisteredVoterAgeDistribution = async (filter) => (
  await request("reporting/overview/registered-voters/age", "POST", normalizeReportingFilter(filter))
);

export const loadRegisteredVoterGeography = async (filter, level = "DEPARTMENT") => (
  await request(
    `reporting/overview/registered-voters/geography?level=${encodeURIComponent(level)}`,
    "POST",
    normalizeReportingFilter(filter),
  )
);

export const loadReportingTimeline = async (filter, interval = "DAY") => (
  await request(
    `reporting/timeline?interval=${encodeURIComponent(interval)}`,
    "POST",
    normalizeReportingFilter(filter),
  )
);

export const loadReportingGeography = async (filter, level = "DEPARTMENT") => (
  await request(
    `reporting/geography?level=${encodeURIComponent(level)}`,
    "POST",
    normalizeReportingFilter(filter),
  )
);

export const loadReportingDemographics = async (filter) => (
  await request("reporting/demographics", "POST", normalizeReportingFilter(filter))
);

export const loadReportingOperators = async (filter) => (
  await request("reporting/operators", "POST", normalizeReportingFilter(filter))
);

export const loadReportingVotingCenters = async (
  filter,
  { offset = 0, limit = 50 } = {},
) => (
  await request(
    `reporting/voting-centers?offset=${Number(offset)}&limit=${Number(limit)}`,
    "POST",
    normalizeReportingFilter(filter),
  )
);

const buildSearchPayload = (query = "", limit = ENTITY_LIMIT, sort = null) => ({
  columns: [],
  query,
  limit,
  offset: 0,
  sort: sort || [
    { selector: "name", direction: "asc" },
    { selector: "code", direction: "asc" },
  ],
});

export const searchReportingEntity = async (entity, query = "") => (
  await request(`${entity}/search`, "POST", buildSearchPayload(query))
);

export const loadDepartments = async () => searchReportingEntity("department");
export const loadConstituencies = async () => searchReportingEntity("constituency");

export const loadArrondissements = async (departmentGid = "") => (
  await searchReportingEntity(
    "arrondissement",
    departmentGid ? `departmentGid==${Number(departmentGid)}` : "",
  )
);

export const loadCommunes = async ({ arrondissementGid, constituencyGid } = {}) => {
  const parts = [];
  if (arrondissementGid) parts.push(`arrondissementGid==${Number(arrondissementGid)}`);
  if (constituencyGid) parts.push(`constituencyGid==${Number(constituencyGid)}`);
  return await searchReportingEntity("commune", parts.join(";"));
};

export const loadSections = async (communeGid) => {
  if (!communeGid) return [];
  return await searchReportingEntity("section", `communeGid==${Number(communeGid)}`);
};

export const loadVotingCenters = async (sectionGid) => {
  if (!sectionGid) return [];
  return await searchReportingEntity("voting-center", `sectionGid==${Number(sectionGid)}`);
};

const escapeRsqlRegexValue = (value) => String(value || "")
  .trim()
  .replace(/\\/g, "\\\\")
  .replace(/"/g, '\\"')
  .replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const operatorQuery = (search) => {
  const value = escapeRsqlRegexValue(search);
  if (!value) return "";
  return ["firstName", "lastName", "nationalId", "username"]
    .map((field) => `${field}=re="(?i).*${value}.*"`)
    .join(",");
};

const operatorName = (account) => {
  const name = `${account?.firstName || ""} ${account?.lastName || ""}`.trim();
  return name || account?.username || account?.nationalId || "Unknown operator";
};

export const searchReportingOperators = async (search) => {
  const trimmed = String(search || "").trim();
  if (trimmed.length < 2) return [];

  const accounts = await request(
    "user-account/search",
    "POST",
    buildSearchPayload(
      operatorQuery(trimmed),
      OPERATOR_LIMIT,
      [
        { selector: "lastName", direction: "asc" },
        { selector: "firstName", direction: "asc" },
        { selector: "username", direction: "asc" },
      ],
    ),
  );

  const byNationalId = new Map();

  for (const account of accounts || []) {
    const nationalId = String(account?.nationalId || account?.national_id || "").trim();
    if (!nationalId) continue;

    const current = byNationalId.get(nationalId) || {
      nationalId,
      name: operatorName(account),
      usernames: [],
      accountCount: 0,
    };

    const candidateName = operatorName(account);
    if (candidateName && current.name === "Unknown operator") current.name = candidateName;

    const username = String(account?.username || "").trim();
    if (username && !current.usernames.includes(username)) current.usernames.push(username);
    current.accountCount += 1;
    byNationalId.set(nationalId, current);
  }

  return [...byNationalId.values()].sort((a, b) => (
    a.name.localeCompare(b.name) || a.nationalId.localeCompare(b.nationalId)
  ));
};
